import React from 'react';
import Layout from '../../components/Layout';
import ApiInspector from '../../components/ApiInspector';
import './GuidaContabilitaPage.css';

// Pagina statica di documentazione: nessuna API chiamata.
const API_DOCS = [] as const;

// Tag inline riutilizzabili per Dare / Avere
const Dare = () => <span className="bop-guida-coge__tag bop-guida-coge__tag--dare">DARE</span>;
const Avere = () => <span className="bop-guida-coge__tag bop-guida-coge__tag--avere">AVERE</span>;

export default function GuidaContabilitaPage() {
  return (
    <Layout>
      <div className="bop-guida-coge">
        <header className="bop-guida-coge__header">
          <h1 className="bop-guida-coge__titolo">Guida contabilità</h1>
          <p className="bop-guida-coge__sottotitolo">
            Guida funzionale completa al reparto di contabilità in BOP: come è
            organizzato il Piano dei conti CO.GE, cosa significano le tipologie dei
            sottoconti, perché esistono più Registri contabili, come si registra in
            partita doppia, come si liquida l'IVA e come si arriva al bilancio italiano.
          </p>
        </header>

        <nav className="bop-guida-coge__indice" aria-label="Indice della guida">
          <h2 className="bop-guida-coge__indice-titolo">Indice</h2>
          <ol className="bop-guida-coge__indice-lista">
            <li><a href="#introduzione">1. Introduzione e mappa del modulo</a></li>
            <li><a href="#mattoni">2. I mattoni della contabilità e come si incastrano</a></li>
            <li><a href="#piano-conti">3. Il Piano dei conti CO.GE — struttura gerarchica</a></li>
            <li><a href="#tipologie-sottoconti">4. Le tipologie di sottoconto (natura e sottotipo)</a></li>
            <li><a href="#registri">5. I Registri contabili — uno o più?</a></li>
            <li><a href="#sezionali">6. I Sezionali IVA</a></li>
            <li><a href="#causali">7. Le Causali contabili</a></li>
            <li><a href="#scritture">8. Le Scritture contabili (partita doppia)</a></li>
            <li><a href="#liquidazione">9. La Liquidazione IVA</a></li>
            <li><a href="#bilancio">10. Come fare un bilancio italiano con il software</a></li>
            <li><a href="#esempi">11. Esempi pratici di registrazione</a></li>
            <li><a href="#errori-comuni">12. Errori comuni</a></li>
            <li><a href="#faq">13. FAQ</a></li>
            <li><a href="#glossario">14. Glossario</a></li>
            <li><a href="#riferimenti">15. Riferimenti</a></li>
          </ol>
        </nav>

        <main className="bop-guida-coge__contenuto">

          {/* 1. INTRODUZIONE */}
          <section id="introduzione" className="bop-guida-coge__sezione">
            <h2 className="bop-guida-coge__sezione-titolo">1. Introduzione e mappa del modulo</h2>
            <p className="bop-guida-coge__paragrafo">
              Tutto ciò che riguarda la contabilità generale (CO.GE) vive sotto il menu
              <strong> Gestione Amministrativa → Contabilità Generale</strong>. È il cuore
              fiscale e civilistico del gestionale: qui si tiene la partita doppia, si
              gestiscono i registri IVA e si prepara la liquidazione periodica.
            </p>
            <p className="bop-guida-coge__paragrafo">
              Il gruppo è multi-società: <strong>ogni dato contabile appartiene a una sola
              società</strong>. Quando lavori, controlla sempre quale società è selezionata
              nella barra in alto: i conti, i registri, le scritture e le liquidazioni che
              vedi sono solo quelli di quella società. Un piano dei conti della società A non
              è visibile né utilizzabile nella società B.
            </p>
            <p className="bop-guida-coge__paragrafo">Il menu "Contabilità Generale" raccoglie sei voci, nell'ordine logico in cui si usano:</p>
            <ul className="bop-guida-coge__lista">
              <li><strong>Piano dei conti CO.GE</strong> — l'elenco gerarchico di tutti i conti (l'ossatura di tutto).</li>
              <li><strong>Sezionali IVA</strong> — i registri IVA per legge (vendite, acquisti, corrispettivi).</li>
              <li><strong>Registri contabili</strong> — il libro giornale unico (sola lettura, automatico).</li>
              <li><strong>Causali contabili</strong> — i modelli di registrazione che velocizzano le scritture.</li>
              <li><strong>Scritture contabili</strong> — le registrazioni vere e proprie in partita doppia.</li>
              <li><strong>Liquidazioni IVA</strong> — il calcolo periodico dell'IVA da versare o a credito.</li>
            </ul>
          </section>

          {/* 2. MATTONI */}
          <section id="mattoni" className="bop-guida-coge__sezione">
            <h2 className="bop-guida-coge__sezione-titolo">2. I mattoni della contabilità e come si incastrano</h2>
            <p className="bop-guida-coge__paragrafo">
              Prima di entrare nel dettaglio conviene capire il quadro d'insieme. Le sei voci
              non sono indipendenti: sono livelli che si appoggiano l'uno sull'altro. Il modo
              più semplice per ricordarseli è associarli a una domanda:
            </p>
            <div className="bop-guida-coge__tabella-wrap">
              <table className="bop-guida-coge__tabella">
                <thead>
                  <tr><th>Elemento</th><th>Risponde alla domanda…</th><th>Esempio</th></tr>
                </thead>
                <tbody>
                  <tr><td><strong>Piano dei conti</strong></td><td>Su <em>quali conti</em> posso muovere i numeri?</td><td>Crediti v/clienti, Cassa, Ricavi di vendita, IVA a debito…</td></tr>
                  <tr><td><strong>Registro contabile</strong></td><td><em>Dove</em> annoto l'operazione (il libro giornale)?</td><td>Libro giornale (unico, automatico)</td></tr>
                  <tr><td><strong>Sezionale IVA</strong></td><td><em>Come</em> numero l'operazione ai fini IVA?</td><td>Sezionale Vendite, Sezionale Acquisti</td></tr>
                  <tr><td><strong>Causale</strong></td><td><em>Che tipo</em> di operazione è (modello pronto)?</td><td>Fattura di vendita, Incasso cliente, Stipendi</td></tr>
                  <tr><td><strong>Scrittura</strong></td><td>La registrazione concreta in <em>Dare/Avere</em></td><td>Fattura #042/2026 ad ACME per 1.220,00 €</td></tr>
                  <tr><td><strong>Liquidazione IVA</strong></td><td><em>Quanta IVA</em> devo versare nel periodo?</td><td>Giugno 2026: 1.500,00 € da versare</td></tr>
                </tbody>
              </table>
            </div>
            <p className="bop-guida-coge__paragrafo">
              In pratica: costruisci una volta il <strong>piano dei conti</strong>, definisci i
              <strong> registri</strong>, i <strong>sezionali</strong> e le <strong>causali</strong>
              (operazioni di configurazione, si fanno all'inizio e poi si ritoccano raramente);
              dopodiché il lavoro quotidiano è registrare <strong>scritture</strong> e, a fine
              periodo, chiudere la <strong>liquidazione IVA</strong>.
            </p>
          </section>

          {/* 3. PIANO DEI CONTI */}
          <section id="piano-conti" className="bop-guida-coge__sezione">
            <h2 className="bop-guida-coge__sezione-titolo">3. Il Piano dei conti CO.GE — struttura gerarchica</h2>
            <p className="bop-guida-coge__paragrafo">
              Il piano dei conti è un <strong>albero a quattro livelli</strong>. I primi tre
              livelli servono solo a <em>raggruppare</em>; solo le foglie (i sottoconti) sono
              <strong> movimentabili</strong>, cioè possono essere usate in una scrittura
              contabile. Non si registra mai su un raggruppamento, ma solo su un sottoconto.
            </p>
            <div className="bop-guida-coge__tabella-wrap">
              <table className="bop-guida-coge__tabella">
                <thead>
                  <tr><th>Livello</th><th>Nome</th><th>Movimentabile?</th><th>Esempio codice</th><th>Esempio descrizione</th></tr>
                </thead>
                <tbody>
                  <tr><td>1</td><td><strong>Classe</strong></td><td>No (radice, porta la natura)</td><td><code>1</code></td><td>Attività</td></tr>
                  <tr><td>2</td><td>Gruppo</td><td>No</td><td><code>1.02</code></td><td>Disponibilità liquide</td></tr>
                  <tr><td>3</td><td>Gruppo</td><td>No</td><td><code>1.02.01</code></td><td>Conti correnti</td></tr>
                  <tr><td>4</td><td><strong>Sottoconto</strong></td><td><strong>Sì</strong> (foglia)</td><td><code>1.02.01.0001</code></td><td>c/c bancario Intesa</td></tr>
                </tbody>
              </table>
            </div>
            <p className="bop-guida-coge__paragrafo">Regole pratiche da conoscere:</p>
            <ul className="bop-guida-coge__lista">
              <li>Il <strong>codice</strong> si compone a segmenti separati da punto (<code>1.02.01.0001</code>). Ogni segmento usa solo lettere e numeri ed è univoco tra i conti "fratelli".</li>
              <li>Il codice completo è <strong>univoco all'interno della società</strong>: due conti non possono avere lo stesso codice anche se stanno su rami diversi.</li>
              <li>Quando crei un figlio scegli se è un <strong>Gruppo</strong> (raggruppamento, può avere figli) o un <strong>Sottoconto</strong> (foglia movimentabile). Questa scelta <strong>non è più modificabile</strong> dopo la creazione.</li>
              <li>La profondità massima è <strong>4 livelli</strong>. Non puoi aggiungere figli sotto un sottoconto, né sotto un conto disattivato.</li>
              <li><strong>Non si sposta</strong> un conto da un genitore a un altro: se serve, lo si disattiva e si ricrea nella posizione giusta.</li>
              <li>Un conto si può <strong>disattivare</strong> solo se non ha figli ancora attivi (disattiva prima i figli). Si può <strong>eliminare</strong> solo se non ha figli ed è un'operazione definitiva: di norma si preferisce disattivare.</li>
            </ul>
            <div className="bop-guida-coge__callout">
              <p className="bop-guida-coge__callout-titolo">Import / Export CSV</p>
              <p>
                Il piano dei conti si può esportare in CSV e re-importare. Per costruire o
                aggiornare in massa un piano dei conti, esporta prima il file come modello:
                l'intestazione attesa è esattamente
                <code> codice;segmento;descrizione;livello;movimentabile;natura;sottotipo;attivo;note</code>.
                L'import lavora in due fasi (anteprima con evidenza di nuovi/aggiornamenti/errori,
                poi conferma), e i padri vengono creati prima dei figli.
              </p>
            </div>
          </section>

          {/* 4. TIPOLOGIE SOTTOCONTI */}
          <section id="tipologie-sottoconti" className="bop-guida-coge__sezione">
            <h2 className="bop-guida-coge__sezione-titolo">4. Le tipologie di sottoconto (natura e sottotipo)</h2>
            <p className="bop-guida-coge__paragrafo">
              È il punto su cui nascono più dubbi in reparto, perché un sottoconto ha
              <strong> due classificazioni diverse</strong> che spesso vengono confuse: la
              <strong> natura</strong> e il <strong>sottotipo</strong>. Sono cose distinte e
              rispondono a domande diverse.
            </p>

            <h3 className="bop-guida-coge__sottotitolo-sezione">4.1 La natura — "che cosa è" il conto in bilancio</h3>
            <p className="bop-guida-coge__paragrafo">
              La natura dice se il conto è patrimoniale o economico, attivo o passivo. È
              definita <strong>solo a livello di Classe</strong> (livello 1) e viene
              <strong> ereditata da tutti i conti sotto di essa</strong>. In pratica scegli la
              natura una volta sola, quando crei la grande classe (es. "Attività"), e tutti i
              suoi discendenti la prendono in automatico. La natura <strong>non è
              modificabile</strong> dopo la creazione della classe.
            </p>
            <div className="bop-guida-coge__tabella-wrap">
              <table className="bop-guida-coge__tabella">
                <thead>
                  <tr><th>Natura</th><th>Significato</th><th>Dove finisce in bilancio</th><th>Esempi di conto</th></tr>
                </thead>
                <tbody>
                  <tr><td><strong>Attività</strong></td><td>Beni e crediti dell'azienda</td><td>Stato Patrimoniale — Attivo</td><td>Crediti v/clienti, Cassa, Banca, Immobili, Impianti</td></tr>
                  <tr><td><strong>Passività</strong></td><td>Debiti dell'azienda</td><td>Stato Patrimoniale — Passivo</td><td>Debiti v/fornitori, Mutui, IVA a debito</td></tr>
                  <tr><td><strong>Patrimonio netto</strong></td><td>Capitale proprio</td><td>Stato Patrimoniale — Patrimonio netto</td><td>Capitale sociale, Riserve, Utile d'esercizio</td></tr>
                  <tr><td><strong>Costi</strong></td><td>Costi e oneri di esercizio</td><td>Conto Economico — Costi</td><td>Materie prime, Stipendi, Ammortamenti, Utenze</td></tr>
                  <tr><td><strong>Ricavi</strong></td><td>Ricavi e proventi</td><td>Conto Economico — Ricavi</td><td>Vendite, Prestazioni, Interessi attivi</td></tr>
                  <tr><td><strong>Conti d'ordine</strong></td><td>Impegni e garanzie fuori partita doppia</td><td>In calce al bilancio (memoria)</td><td>Garanzie ricevute, Beni di terzi in custodia</td></tr>
                </tbody>
              </table>
            </div>

            <h3 className="bop-guida-coge__sottotitolo-sezione">4.2 Il sottotipo — "a che cosa serve" il sottoconto</h3>
            <p className="bop-guida-coge__paragrafo">
              Il sottotipo si imposta <strong>solo sui sottoconti movimentabili</strong> (le
              foglie) e serve a collegare il conto a un'anagrafica o a un comportamento
              specifico (cliente, fornitore, banca, IVA…). A differenza della natura, il
              sottotipo <strong>si può modificare</strong> anche dopo la creazione. Se non sai
              cosa scegliere, il valore corretto è <strong>Generico</strong>.
            </p>
            <div className="bop-guida-coge__tabella-wrap">
              <table className="bop-guida-coge__tabella">
                <thead>
                  <tr><th>Sottotipo</th><th>A cosa serve</th><th>Natura tipica</th><th>Collegamento</th></tr>
                </thead>
                <tbody>
                  <tr><td><strong>Generico</strong></td><td>Conto standard, senza collegamenti speciali (è il default)</td><td>Qualsiasi (spesso Costi o Ricavi)</td><td>Nessuno</td></tr>
                  <tr><td><strong>Cliente</strong></td><td>Credito verso clienti</td><td>Attività</td><td>Anagrafica nominativi (clienti)</td></tr>
                  <tr><td><strong>Fornitore</strong></td><td>Debito verso fornitori</td><td>Passività</td><td>Anagrafica nominativi (fornitori)</td></tr>
                  <tr><td><strong>Banca</strong></td><td>Conto corrente bancario</td><td>Attività (Passività se scoperto)</td><td>Anagrafica banche aziendali</td></tr>
                  <tr><td><strong>Cassa</strong></td><td>Contante / fondo cassa</td><td>Attività</td><td>Nessuno</td></tr>
                  <tr><td><strong>IVA</strong></td><td>IVA a debito / a credito — usato dalla liquidazione</td><td>Passività (a debito) o Attività (a credito)</td><td>Codici IVA</td></tr>
                  <tr><td><strong>Erario</strong></td><td>Debiti verso lo Stato (imposte, ritenute)</td><td>Passività (Attività per crediti d'imposta)</td><td>Nessuno</td></tr>
                  <tr><td><strong>Personale</strong></td><td>Rapporti con i dipendenti (stipendi, anticipi)</td><td>Costi (stipendi) e Passività (retribuzioni da pagare)</td><td>Anagrafica dipendenti</td></tr>
                  <tr><td><strong>Patrimonio</strong></td><td>Immobilizzazioni e cespiti</td><td>Attività</td><td>Nessuno</td></tr>
                </tbody>
              </table>
            </div>
            <div className="bop-guida-coge__callout">
              <p className="bop-guida-coge__callout-titolo">Natura e sottotipo non sono legati dal sistema</p>
              <p>
                La colonna "Natura tipica" indica l'abbinamento <strong>contabilmente
                corretto</strong>, ma non è un vincolo imposto dal software: il sottotipo si
                sceglie liberamente sul sottoconto e la natura resta quella ereditata dalla
                Classe. Sta a te collocare il conto sotto la Classe giusta. Attenzione in
                particolare al sottotipo <strong>Patrimonio</strong>: nonostante il nome serve
                per le <strong>immobilizzazioni</strong> (natura <strong>Attività</strong>), non
                per i conti di Patrimonio netto come capitale e riserve.
              </p>
            </div>
            <div className="bop-guida-coge__callout bop-guida-coge__callout--warning">
              <p className="bop-guida-coge__callout-titolo">Il sottotipo IVA è quello da non sbagliare</p>
              <p>
                I conti marcati con sottotipo <strong>IVA</strong> sono quelli che la
                <strong> Liquidazione IVA</strong> va a leggere per calcolare l'imposta del
                periodo. Se registri l'IVA su un conto generico anziché su un conto di
                sottotipo IVA, quell'importo <strong>non entrerà</strong> nella liquidazione.
              </p>
            </div>

            <h3 className="bop-guida-coge__sottotitolo-sezione">4.3 Il flag "Mostra in anagrafica articoli"</h3>
            <p className="bop-guida-coge__paragrafo">
              Sui sottoconti movimentabili c'è un'opzione <strong>"Mostra in anagrafica
              articoli"</strong>. Se attiva, quel sottoconto diventa selezionabile come conto
              di default nella scheda dell'articolo. È utile per i conti di ricavo/costo che
              vuoi collegare agli articoli, così durante la fatturazione il conto giusto è già
              proposto. È un'opzione facoltativa, di default disattivata.
            </p>

            <h3 className="bop-guida-coge__sottotitolo-sezione">4.4 Come riconoscere un sottoconto a colpo d'occhio</h3>
            <p className="bop-guida-coge__paragrafo">
              Nell'elenco del piano dei conti ogni riga mostra dei badge: un tag <strong>Tipo</strong>
              ("Classe", "Gruppo · liv. X" oppure "Sottoconto"), un badge colorato per la
              <strong> natura</strong> e, se diverso da Generico, un chip per il
              <strong> sottotipo</strong>. Hai anche filtri per natura, per sottotipo, una
              casella "Solo sottoconti" e una per mostrare i conti disattivati.
            </p>
          </section>

          {/* 5. REGISTRI */}
          <section id="registri" className="bop-guida-coge__sezione">
            <h2 className="bop-guida-coge__sezione-titolo">5. I Registri contabili — uno o più?</h2>

            <div className="bop-guida-coge__callout bop-guida-coge__callout--warning">
              <p className="bop-guida-coge__callout-titolo">Mettiamo subito le cose in chiaro</p>
              <p>
                In Italia <strong>non c'è alcun obbligo di tenere più "registri contabili"
                generali</strong>. La contabilità in partita doppia si tiene in un
                <strong> unico libro giornale</strong> (art. 2216 c.c.), cronologico, con una
                <strong> sola numerazione progressiva</strong>. Chi dice che la prassi è "un
                registro unico numerato progressivamente" ha ragione. L'unica separazione
                <em> imposta dalla legge</em> è quella dei <strong>registri IVA</strong>, che in
                BOP non sono i Registri ma i <strong>Sezionali IVA</strong> (vedi sezione 6). I
                registri multipli di BOP sono uno <strong>strumento organizzativo facoltativo</strong>,
                non un requisito normativo.
              </p>
            </div>

            <p className="bop-guida-coge__paragrafo">
              Un registro contabile in BOP è un <strong>libro di prima nota</strong>: il luogo
              dove annoti l'operazione. Ogni scrittura appartiene a un registro e riceve da quel
              registro una numerazione progressiva annuale. Per lavorare correttamente
              <strong> basta un solo registro di tipo Giornale</strong>: è la configurazione
              minima e perfettamente legittima.
            </p>

            <h3 className="bop-guida-coge__sottotitolo-sezione">5.1 Le tre cose da non confondere</h3>
            <div className="bop-guida-coge__tabella-wrap">
              <table className="bop-guida-coge__tabella">
                <thead>
                  <tr><th>Cosa</th><th>Quante</th><th>Obbligo di legge?</th><th>Dove in BOP</th></tr>
                </thead>
                <tbody>
                  <tr><td>Libro giornale</td><td><strong>Uno</strong>, numerazione unica</td><td>Sì (art. 2216 c.c.)</td><td>Registro contabile di tipo <em>Giornale</em></td></tr>
                  <tr><td>Registri IVA (vendite / acquisti / corrispettivi)</td><td>Separati per tipo</td><td><strong>Sì</strong> (DPR 633/72, artt. 23-25)</td><td><em>Sezionali IVA</em> (sezione 6)</td></tr>
                  <tr><td>Cassa, conti correnti</td><td>Uno per cassa / per c/c</td><td><strong>No</strong> (strumenti gestionali)</td><td><em>Conti</em> del piano dei conti (sottotipo Cassa/Banca)</td></tr>
                </tbody>
              </table>
            </div>
            <p className="bop-guida-coge__paragrafo">
              In particolare, nella contabilità ordinaria <strong>non esiste un obbligo di
              "libro cassa" o "libro banca"</strong>: la prima nota di cassa e i registri di
              banca sono strumenti gestionali, non libri fiscali. Tenerli separati è una scelta,
              non un dovere.
            </p>

            <h3 className="bop-guida-coge__sottotitolo-sezione">5.2 Come lo gestisce BOP</h3>
            <p className="bop-guida-coge__paragrafo">
              Coerentemente con questa regola, BOP adotta il modello più semplice e conforme:
              <strong> un solo registro Giornale per società</strong>, creato automaticamente e
              mostrato in <strong>sola lettura</strong> nella pagina <em>Registri contabili</em>.
              Non si creano né si gestiscono più registri: tutte le scritture confluiscono
              nell'unico giornale, con un'unica numerazione progressiva.
            </p>
            <p className="bop-guida-coge__paragrafo">
              Di conseguenza <strong>cassa e banca non sono registri</strong>: sono
              <strong> conti</strong> del piano dei conti (sottoconti di sottotipo Cassa/Banca).
              Il collegamento di un conto corrente alla <strong>Banca aziendale</strong> si
              imposta direttamente sul sottoconto, nella pagina <em>Piano dei conti CO.GE</em>
              (campo "Banca collegata", visibile quando il sottotipo è Banca). È lì che vivrà,
              in futuro, la riconciliazione bancaria.
            </p>
            <div className="bop-guida-coge__callout">
              <p className="bop-guida-coge__callout-titolo">In pratica</p>
              <p>
                La pagina <em>Registri contabili</em> ti mostra il Giornale e la sua numerazione
                corrente, ma non ci devi fare nulla: è informativa. Quando registri una scrittura,
                il giornale è già selezionato per te. Per gestire cassa e banche, lavori sul
                <strong> Piano dei conti</strong> (creando i sottoconti) e su
                <strong> Banche aziendali</strong> (anagrafica dei c/c).
              </p>
            </div>
          </section>

          {/* 6. SEZIONALI IVA */}
          <section id="sezionali" className="bop-guida-coge__sezione">
            <h2 className="bop-guida-coge__sezione-titolo">6. I Sezionali IVA</h2>
            <p className="bop-guida-coge__paragrafo">
              Il sezionale IVA è il <strong>registro IVA previsto per legge</strong>. Tiene
              traccia delle operazioni rilevanti ai fini IVA con una <strong>numerazione
              propria</strong> indipendente da quella del registro contabile. Per questo una
              stessa fattura di vendita riceve <strong>due numeri</strong>: uno dal registro
              (es. il giornale) e uno dal sezionale su cui è registrata.
            </p>
            <p className="bop-guida-coge__paragrafo">
              In BOP il sezionale <strong>non ha una tipologia</strong>: è identificato da
              <strong> codice e descrizione</strong>, che scegli tu in base a come vuoi
              organizzare i registri IVA. Esempi tipici:
            </p>
            <div className="bop-guida-coge__tabella-wrap">
              <table className="bop-guida-coge__tabella">
                <thead>
                  <tr><th>Esempio</th><th>Registra</th><th>Effetto IVA</th></tr>
                </thead>
                <tbody>
                  <tr><td><strong>VND — Vendite</strong></td><td>Fatture emesse e note di credito emesse</td><td>IVA a <strong>debito</strong> (da versare)</td></tr>
                  <tr><td><strong>ACQ — Acquisti</strong></td><td>Fatture ricevute e note di credito ricevute</td><td>IVA a <strong>credito</strong> (detraibile)</td></tr>
                  <tr><td><strong>COR — Corrispettivi</strong></td><td>Incassi senza fattura (scontrini, ricevute)</td><td>IVA a debito sui corrispettivi</td></tr>
                </tbody>
              </table>
            </div>
            <p className="bop-guida-coge__paragrafo">
              Puoi creare quanti sezionali ti servono (es. due sezionali vendite Italia/Estero)
              e marcarne <strong>uno come default</strong>: viene usato dalle registrazioni che
              non ne specificano uno. I sezionali sono la base di partenza della liquidazione
              IVA periodica.
            </p>
          </section>

          {/* 7. CAUSALI */}
          <section id="causali" className="bop-guida-coge__sezione">
            <h2 className="bop-guida-coge__sezione-titolo">7. Le Causali contabili</h2>
            <p className="bop-guida-coge__paragrafo">
              Una causale è un <strong>modello di scrittura</strong>: prepara in anticipo la
              struttura della registrazione per un tipo di operazione ricorrente, così non
              devi reinserire ogni volta gli stessi conti. Quando crei una scrittura e scegli
              una causale, il sistema precompila il registro, l'eventuale sezionale IVA e le
              righe Dare/Avere; a te resta da inserire importi, nominativo e dettagli.
            </p>
            <p className="bop-guida-coge__paragrafo">
              Ogni causale ha un <strong>tipo</strong> (immutabile) che ne descrive la
              funzione. I tipi disponibili coprono tutto il ciclo contabile:
            </p>
            <div className="bop-guida-coge__tabella-wrap">
              <table className="bop-guida-coge__tabella">
                <thead>
                  <tr><th>Gruppo</th><th>Tipi di causale</th><th>IVA</th></tr>
                </thead>
                <tbody>
                  <tr><td>Ciclo attivo</td><td>Fattura di vendita, Nota di credito emessa, Incasso cliente</td><td>Sì (le prime due)</td></tr>
                  <tr><td>Ciclo passivo</td><td>Fattura di acquisto, Nota di credito ricevuta, Pagamento fornitore</td><td>Sì (le prime due)</td></tr>
                  <tr><td>Tesoreria</td><td>Giroconto (es. banca → cassa)</td><td>No</td></tr>
                  <tr><td>Assestamenti</td><td>Ratei e risconti, Ammortamenti, Rivalutazioni/svalutazioni</td><td>No</td></tr>
                  <tr><td>Personale / Erario</td><td>Stipendi, Ritenute</td><td>No</td></tr>
                  <tr><td>Apertura / Chiusura</td><td>Apertura esercizio, Chiusura esercizio</td><td>No</td></tr>
                  <tr><td>Analitica / Altro</td><td>Ribaltamenti, Altro (personalizzata)</td><td>No</td></tr>
                </tbody>
              </table>
            </div>
            <p className="bop-guida-coge__paragrafo">
              Una causale che gestisce IVA deve puntare a un <strong>sezionale IVA
              default</strong>: lo scegli tu tra quelli configurati per la società (es. la
              causale "Fattura di vendita" punterà al sezionale vendite). Ogni riga del
              modello indica come ottenere il conto:
            </p>
            <ul className="bop-guida-coge__lista">
              <li><strong>Fisso</strong>: conto predefinito nella causale (es. un conto IVA o un conto ricavi specifico).</li>
              <li><strong>Da anagrafica</strong>: il conto del cliente/fornitore indicato nel documento.</li>
              <li><strong>Da codice IVA</strong>: il conto IVA dedotto dall'aliquota scelta sulla fattura.</li>
              <li><strong>Scelta utente</strong>: il conto lo decidi al momento della registrazione.</li>
            </ul>
            <div className="bop-guida-coge__callout">
              <p className="bop-guida-coge__callout-titolo">Causali "da completare"</p>
              <p>
                Una causale può nascere "scheletro" e venir segnalata come <strong>da
                completare</strong> finché non le assegni i conti fissi mancanti, il sezionale
                IVA (se gestisce IVA) o il registro di default. Completa questi dati prima di
                usarla in una scrittura reale.
              </p>
            </div>
          </section>

          {/* 8. SCRITTURE */}
          <section id="scritture" className="bop-guida-coge__sezione">
            <h2 className="bop-guida-coge__sezione-titolo">8. Le Scritture contabili (partita doppia)</h2>
            <p className="bop-guida-coge__paragrafo">
              La scrittura è la registrazione vera e propria. È fatta di una
              <strong> testata</strong> (i dati generali) e di un elenco di
              <strong> righe</strong> in partita doppia, ciascuna su un sottoconto, con un
              importo in <Dare /> oppure in <Avere /> (mai entrambi sulla stessa riga).
            </p>
            <div className="bop-guida-coge__tabella-wrap">
              <table className="bop-guida-coge__tabella">
                <thead><tr><th>Sezione</th><th>Campi principali</th></tr></thead>
                <tbody>
                  <tr><td><strong>Testata</strong></td><td>Data di competenza, registro, causale (facoltativa), sezionale IVA (se la causale gestisce IVA), nominativo, numero e data documento, descrizione.</td></tr>
                  <tr><td><strong>Righe</strong></td><td>Sottoconto CO.GE, importo in Dare o Avere, descrizione, codice IVA e imponibile di riferimento (sulle righe IVA), eventuale nominativo e conto analitico.</td></tr>
                </tbody>
              </table>
            </div>

            <h3 className="bop-guida-coge__sottotitolo-sezione">8.1 La regola d'oro: la quadratura</h3>
            <p className="bop-guida-coge__paragrafo">
              In partita doppia <strong>il totale Dare deve essere uguale al totale Avere</strong>
              (con tolleranza di un centesimo). Mentre inserisci le righe il sistema mostra in
              tempo reale la somma del Dare, la somma dell'Avere e lo sbilancio. Una scrittura
              non quadrata <strong>non può essere confermata</strong>.
            </p>

            <h3 className="bop-guida-coge__sottotitolo-sezione">8.2 Gli stati della scrittura</h3>
            <div className="bop-guida-coge__tabella-wrap">
              <table className="bop-guida-coge__tabella">
                <thead><tr><th>Stato</th><th>Significato</th><th>Cosa puoi fare</th></tr></thead>
                <tbody>
                  <tr><td><strong>Bozza</strong></td><td>In lavorazione, non ancora numerata</td><td>Modificare ed eliminare liberamente</td></tr>
                  <tr><td><strong>Confermata</strong></td><td>Numerata e ufficiale (riceve numero di registro e, se IVA, di sezionale)</td><td>Sola lettura; bloccata se il periodo IVA è già definitivo</td></tr>
                  <tr><td><strong>Stornata</strong></td><td>Neutralizzata da uno storno</td><td>Sola lettura</td></tr>
                </tbody>
              </table>
            </div>
            <p className="bop-guida-coge__paragrafo">
              Per confermare servono: scrittura quadrata, almeno due righe e periodo IVA non
              ancora reso definitivo. Alla conferma viene assegnato il numero progressivo
              dell'anno della data.
            </p>

            <h3 className="bop-guida-coge__sottotitolo-sezione">8.3 Correggere una scrittura confermata: lo storno</h3>
            <p className="bop-guida-coge__paragrafo">
              Una scrittura confermata non si cancella: si <strong>storna</strong>. Lo storno
              crea una <strong>scrittura inversa</strong> (Dare e Avere scambiati) che annulla
              gli effetti dell'originale; l'originale passa allo stato "Stornata". È il modo
              corretto e tracciabile per correggere un errore, perché lascia traccia sia
              dell'operazione sbagliata sia della sua rettifica.
            </p>
          </section>

          {/* 9. LIQUIDAZIONE IVA */}
          <section id="liquidazione" className="bop-guida-coge__sezione">
            <h2 className="bop-guida-coge__sezione-titolo">9. La Liquidazione IVA</h2>
            <p className="bop-guida-coge__paragrafo">
              La liquidazione IVA è il calcolo periodico — <strong>mensile o trimestrale</strong>,
              a seconda della società — di quanta IVA si deve versare o quanta resta a credito.
              Il sistema legge le righe IVA delle scritture <strong>confermate</strong> del
              periodo (quelle su conti di sottotipo IVA, con codice IVA e imponibile valorizzati)
              e le aggrega.
            </p>
            <p className="bop-guida-coge__paragrafo">Il calcolo segue questo schema:</p>
            <ul className="bop-guida-coge__lista">
              <li><strong>IVA a debito</strong> = IVA sulle vendite del periodo.</li>
              <li><strong>IVA a credito</strong> = IVA sugli acquisti del periodo.</li>
              <li><strong>Saldo del periodo</strong> = IVA a debito − IVA a credito.</li>
              <li><strong>Credito pregresso</strong> = credito riportato dal periodo precedente reso definitivo.</li>
              <li>Se (saldo − credito pregresso) è positivo → <strong>IVA da versare</strong>; se è negativo → <strong>credito da riportare</strong> al periodo successivo.</li>
            </ul>
            <p className="bop-guida-coge__paragrafo">
              Una liquidazione nasce <strong>Aperta</strong> e poi viene resa
              <strong> Definitiva</strong>. Prima di renderla definitiva il sistema verifica
              che: non ci siano scritture in bozza nel periodo, tutte le righe IVA siano
              complete e il periodo precedente sia già definitivo (le liquidazioni formano una
              <strong> catena ordinata</strong>, necessaria per il riporto dei crediti). Quando
              diventa definitiva, i totali sono congelati e le scritture IVA di quel periodo non
              sono più modificabili. Se serve correggere, si può <strong>riaprire</strong> una
              liquidazione, ma solo se quella successiva non è ancora definitiva.
            </p>
          </section>

          {/* 10. BILANCIO */}
          <section id="bilancio" className="bop-guida-coge__sezione">
            <h2 className="bop-guida-coge__sezione-titolo">10. Come fare un bilancio italiano con il software</h2>

            <div className="bop-guida-coge__callout bop-guida-coge__callout--warning">
              <p className="bop-guida-coge__callout-titolo">Stato attuale della funzione</p>
              <p>
                Ad oggi il modulo CO.GE <strong>non genera ancora automaticamente</strong> un
                bilancio di verifica, uno Stato Patrimoniale o un Conto Economico riclassificati.
                Tutti i <em>dati</em> necessari però esistono già (piano dei conti con la natura,
                scritture confermate con Dare/Avere): il bilancio si ottiene aggregando questi
                dati. Qui sotto trovi sia la metodologia sia come arrivarci con gli strumenti
                attuali. La stampa di bilancio automatica è una funzione in roadmap.
              </p>
            </div>

            <h3 className="bop-guida-coge__sottotitolo-sezione">10.1 Cos'è il bilancio italiano</h3>
            <p className="bop-guida-coge__paragrafo">
              Il bilancio d'esercizio civilistico (artt. 2424 e 2425 del Codice Civile) è
              composto da:
            </p>
            <ul className="bop-guida-coge__lista">
              <li><strong>Stato Patrimoniale</strong>: la fotografia di attività, passività e patrimonio netto a una certa data.</li>
              <li><strong>Conto Economico</strong>: costi e ricavi del periodo, il cui saldo è l'utile o la perdita d'esercizio.</li>
              <li><strong>Nota integrativa</strong> (e, per le società maggiori, rendiconto finanziario): documento descrittivo, fuori dal perimetro della partita doppia.</li>
            </ul>

            <h3 className="bop-guida-coge__sottotitolo-sezione">10.2 Il ponte: la natura dei conti diventa il bilancio</h3>
            <p className="bop-guida-coge__paragrafo">
              Qui torna utile la natura vista al punto 4: è esattamente la chiave che riclassifica
              ogni saldo nel posto giusto del bilancio.
            </p>
            <div className="bop-guida-coge__tabella-wrap">
              <table className="bop-guida-coge__tabella">
                <thead><tr><th>Natura del conto</th><th>Documento</th><th>Sezione</th><th>Saldo tipico</th></tr></thead>
                <tbody>
                  <tr><td>Attività</td><td>Stato Patrimoniale</td><td>Attivo</td><td>Dare</td></tr>
                  <tr><td>Passività</td><td>Stato Patrimoniale</td><td>Passivo</td><td>Avere</td></tr>
                  <tr><td>Patrimonio netto</td><td>Stato Patrimoniale</td><td>Patrimonio netto</td><td>Avere</td></tr>
                  <tr><td>Costi</td><td>Conto Economico</td><td>Costi</td><td>Dare</td></tr>
                  <tr><td>Ricavi</td><td>Conto Economico</td><td>Ricavi</td><td>Avere</td></tr>
                </tbody>
              </table>
            </div>

            <h3 className="bop-guida-coge__sottotitolo-sezione">10.3 Procedura passo-passo</h3>
            <ol className="bop-guida-coge__lista">
              <li><strong>Registra e conferma tutte le scritture</strong> del periodo, comprese le scritture di assestamento (ratei, risconti, ammortamenti, rimanenze) tipiche del fine esercizio.</li>
              <li><strong>Costruisci il bilancio di verifica</strong>: per ogni sottoconto somma i Dare e gli Avere delle scritture confermate e ricava il saldo. Il totale dei saldi Dare deve uguagliare il totale dei saldi Avere (è la prova della quadratura generale).</li>
              <li><strong>Riclassifica per natura</strong>: i saldi dei conti Attività/Passività/Patrimonio netto formano lo Stato Patrimoniale; quelli dei conti Costi/Ricavi formano il Conto Economico.</li>
              <li><strong>Calcola il risultato d'esercizio</strong>: Ricavi totali − Costi totali. Un risultato positivo è l'<strong>utile</strong>, negativo è la <strong>perdita</strong>. Questo importo chiude il Conto Economico e compare nel Patrimonio netto dello Stato Patrimoniale, facendolo quadrare.</li>
              <li><strong>Archivia il bilancio ufficiale</strong>: il PDF del bilancio depositato si carica nella sezione <em>Management → Upload Documenti → Bilanci</em>.</li>
            </ol>

            <h3 className="bop-guida-coge__sottotitolo-sezione">10.4 Esempio numerico minimo</h3>
            <p className="bop-guida-coge__paragrafo">
              Supponiamo una società i cui conti, dopo tutte le scritture confermate
              dell'esercizio, presentano questi saldi (importi in euro). Poiché ogni scrittura è
              quadrata, anche il <strong>bilancio di verifica complessivo è in pareggio</strong>
              (totale Dare = totale Avere = 205.000):
            </p>
            <div className="bop-guida-coge__tabella-wrap">
              <table className="bop-guida-coge__tabella">
                <thead><tr><th>Conto</th><th>Natura</th><th className="num">Dare</th><th className="num">Avere</th></tr></thead>
                <tbody>
                  <tr><td>Immobili</td><td>Attività</td><td className="num">80.000,00</td><td className="num">—</td></tr>
                  <tr><td>Banca c/c</td><td>Attività</td><td className="num">60.000,00</td><td className="num">—</td></tr>
                  <tr><td>Debiti v/fornitori</td><td>Passività</td><td className="num">—</td><td className="num">15.000,00</td></tr>
                  <tr><td>Capitale sociale</td><td>Patrimonio netto</td><td className="num">—</td><td className="num">90.000,00</td></tr>
                  <tr><td>Materie prime</td><td>Costi</td><td className="num">40.000,00</td><td className="num">—</td></tr>
                  <tr><td>Stipendi</td><td>Costi</td><td className="num">25.000,00</td><td className="num">—</td></tr>
                  <tr><td>Ricavi di vendita</td><td>Ricavi</td><td className="num">—</td><td className="num">100.000,00</td></tr>
                </tbody>
                <tfoot>
                  <tr><td colSpan={2}>Totali</td><td className="num">205.000,00</td><td className="num">205.000,00</td></tr>
                </tfoot>
              </table>
            </div>
            <p className="bop-guida-coge__paragrafo">
              Ora si separano i conti per natura. I conti <strong>economici</strong> (Costi e
              Ricavi) determinano il risultato: Ricavi 100.000 − Costi 65.000 (40.000 + 25.000) =
              <strong> utile d'esercizio di 35.000</strong>. Questo utile confluisce nel
              Patrimonio netto e fa quadrare lo Stato Patrimoniale, formato dai soli conti
              <strong> patrimoniali</strong>:
            </p>
            <div className="bop-guida-coge__tabella-wrap">
              <table className="bop-guida-coge__tabella">
                <thead><tr><th>Stato Patrimoniale — Attivo</th><th className="num">€</th><th>Passivo + PN</th><th className="num">€</th></tr></thead>
                <tbody>
                  <tr><td>Immobili</td><td className="num">80.000,00</td><td>Debiti v/fornitori</td><td className="num">15.000,00</td></tr>
                  <tr><td>Banca c/c</td><td className="num">60.000,00</td><td>Capitale sociale</td><td className="num">90.000,00</td></tr>
                  <tr><td></td><td className="num"></td><td>Utile d'esercizio</td><td className="num">35.000,00</td></tr>
                </tbody>
                <tfoot>
                  <tr><td>Totale attivo</td><td className="num">140.000,00</td><td>Totale passivo + PN</td><td className="num">140.000,00</td></tr>
                </tfoot>
              </table>
            </div>
            <div className="bop-guida-coge__callout">
              <p className="bop-guida-coge__callout-titolo">Nota sull'esempio</p>
              <p>
                Il punto chiave: il bilancio di verifica è <strong>sempre</strong> in pareggio
                (è la prova della partita doppia). Il risultato d'esercizio non è uno "sbilancio",
                ma nasce dalla differenza tra Ricavi e Costi; è quell'utile a far quadrare lo
                Stato Patrimoniale una volta tolti i conti economici. L'esempio è semplificato
                (mancano IVA, crediti v/clienti e cassa per non appesantirlo): in un caso reale
                l'attivo includerà anche crediti, cassa e IVA a credito, e il passivo l'IVA a
                debito e gli altri debiti.
              </p>
            </div>
          </section>

          {/* 11. ESEMPI PRATICI */}
          <section id="esempi" className="bop-guida-coge__sezione">
            <h2 className="bop-guida-coge__sezione-titolo">11. Esempi pratici di registrazione</h2>
            <p className="bop-guida-coge__paragrafo">
              Quattro registrazioni tipiche, con le righe in partita doppia. In tutte la somma
              del <Dare /> uguaglia la somma dell'<Avere />.
            </p>

            <h3 className="bop-guida-coge__sottotitolo-sezione">11.1 Fattura di vendita (imponibile 1.000 + IVA 22%)</h3>
            <p className="bop-guida-coge__paragrafo">
              Causale: <em>Fattura di vendita</em>; registro: <em>Giornale</em>; sezionale:
              <em> Vendite</em>.
            </p>
            <div className="bop-guida-coge__tabella-wrap">
              <table className="bop-guida-coge__tabella">
                <thead><tr><th>Sottoconto</th><th>Sottotipo</th><th className="num">Dare</th><th className="num">Avere</th></tr></thead>
                <tbody>
                  <tr><td>Crediti v/clienti</td><td>Cliente</td><td className="num">1.220,00</td><td className="num">—</td></tr>
                  <tr><td>Ricavi di vendita</td><td>Generico</td><td className="num">—</td><td className="num">1.000,00</td></tr>
                  <tr><td>IVA a debito</td><td>IVA</td><td className="num">—</td><td className="num">220,00</td></tr>
                </tbody>
                <tfoot><tr><td colSpan={2}>Totali</td><td className="num">1.220,00</td><td className="num">1.220,00</td></tr></tfoot>
              </table>
            </div>

            <h3 className="bop-guida-coge__sottotitolo-sezione">11.2 Fattura di acquisto (imponibile 500 + IVA 22%)</h3>
            <p className="bop-guida-coge__paragrafo">
              Causale: <em>Fattura di acquisto</em>; registro: <em>Giornale</em>; sezionale:
              <em> Acquisti</em>.
            </p>
            <div className="bop-guida-coge__tabella-wrap">
              <table className="bop-guida-coge__tabella">
                <thead><tr><th>Sottoconto</th><th>Sottotipo</th><th className="num">Dare</th><th className="num">Avere</th></tr></thead>
                <tbody>
                  <tr><td>Materie prime</td><td>Generico</td><td className="num">500,00</td><td className="num">—</td></tr>
                  <tr><td>IVA a credito</td><td>IVA</td><td className="num">110,00</td><td className="num">—</td></tr>
                  <tr><td>Debiti v/fornitori</td><td>Fornitore</td><td className="num">—</td><td className="num">610,00</td></tr>
                </tbody>
                <tfoot><tr><td colSpan={2}>Totali</td><td className="num">610,00</td><td className="num">610,00</td></tr></tfoot>
              </table>
            </div>

            <h3 className="bop-guida-coge__sottotitolo-sezione">11.3 Incasso di un cliente tramite banca</h3>
            <p className="bop-guida-coge__paragrafo">
              Causale: <em>Incasso cliente</em>; registro: <em>Banca</em> (nessuna IVA, è un
              semplice movimento finanziario).
            </p>
            <div className="bop-guida-coge__tabella-wrap">
              <table className="bop-guida-coge__tabella">
                <thead><tr><th>Sottoconto</th><th>Sottotipo</th><th className="num">Dare</th><th className="num">Avere</th></tr></thead>
                <tbody>
                  <tr><td>Banca c/c</td><td>Banca</td><td className="num">1.220,00</td><td className="num">—</td></tr>
                  <tr><td>Crediti v/clienti</td><td>Cliente</td><td className="num">—</td><td className="num">1.220,00</td></tr>
                </tbody>
                <tfoot><tr><td colSpan={2}>Totali</td><td className="num">1.220,00</td><td className="num">1.220,00</td></tr></tfoot>
              </table>
            </div>

            <h3 className="bop-guida-coge__sottotitolo-sezione">11.4 Registrazione stipendi (semplificata)</h3>
            <p className="bop-guida-coge__paragrafo">
              Causale: <em>Stipendi</em>; registro: <em>Giornale</em> (nessuna IVA).
            </p>
            <div className="bop-guida-coge__tabella-wrap">
              <table className="bop-guida-coge__tabella">
                <thead><tr><th>Sottoconto</th><th>Sottotipo</th><th className="num">Dare</th><th className="num">Avere</th></tr></thead>
                <tbody>
                  <tr><td>Salari e stipendi</td><td>Personale</td><td className="num">3.000,00</td><td className="num">—</td></tr>
                  <tr><td>Erario c/ritenute</td><td>Erario</td><td className="num">—</td><td className="num">600,00</td></tr>
                  <tr><td>Dipendenti c/retribuzioni</td><td>Personale</td><td className="num">—</td><td className="num">2.400,00</td></tr>
                </tbody>
                <tfoot><tr><td colSpan={2}>Totali</td><td className="num">3.000,00</td><td className="num">3.000,00</td></tr></tfoot>
              </table>
            </div>
          </section>

          {/* 12. ERRORI COMUNI */}
          <section id="errori-comuni" className="bop-guida-coge__sezione">
            <h2 className="bop-guida-coge__sezione-titolo">12. Errori comuni</h2>

            <h3 className="bop-guida-coge__sottotitolo-sezione">Confondere natura e sottotipo</h3>
            <p className="bop-guida-coge__paragrafo">
              La <strong>natura</strong> dice dove va il conto in bilancio (Attività, Passività,
              Patrimonio netto, Costi, Ricavi) e si sceglie una sola volta sulla Classe. Il
              <strong> sottotipo</strong> dice a cosa serve la singola foglia (Cliente,
              Fornitore, Banca, IVA…). Sono due cose diverse: un conto di natura Attività può
              avere sottotipo Cliente, Banca o Cassa.
            </p>

            <h3 className="bop-guida-coge__sottotitolo-sezione">Registrare l'IVA su un conto non di sottotipo IVA</h3>
            <p className="bop-guida-coge__paragrafo">
              La liquidazione legge solo le righe sui conti di sottotipo IVA con codice IVA e
              imponibile valorizzati. Se sbagli conto, l'IVA non entra nel calcolo del periodo.
            </p>

            <h3 className="bop-guida-coge__sottotitolo-sezione">Provare a registrare su un raggruppamento</h3>
            <p className="bop-guida-coge__paragrafo">
              Si registra solo sui sottoconti (foglie movimentabili). Classi e gruppi servono
              solo a organizzare: non si possono usare nelle righe di scrittura.
            </p>

            <h3 className="bop-guida-coge__sottotitolo-sezione">Cancellare invece di stornare</h3>
            <p className="bop-guida-coge__paragrafo">
              Una scrittura confermata non si cancella. Per correggerla si usa lo storno, che
              crea la scrittura inversa e mantiene la tracciabilità. Le bozze, invece, si
              possono eliminare liberamente.
            </p>

            <h3 className="bop-guida-coge__sottotitolo-sezione">Rendere definitiva la liquidazione con bozze aperte</h3>
            <p className="bop-guida-coge__paragrafo">
              Prima di rendere definitivo un periodo IVA, conferma tutte le scritture del
              periodo e completa le righe IVA. E ricorda che i periodi vanno chiusi in ordine:
              non puoi rendere definitivo un mese se quello precedente è ancora aperto.
            </p>

            <h3 className="bop-guida-coge__sottotitolo-sezione">Lavorare nella società sbagliata</h3>
            <p className="bop-guida-coge__paragrafo">
              Controlla sempre la società selezionata nella barra in alto prima di creare conti,
              registri o scritture: ogni dato appartiene a una sola società.
            </p>
          </section>

          {/* 13. FAQ */}
          <section id="faq" className="bop-guida-coge__sezione">
            <h2 className="bop-guida-coge__sezione-titolo">13. FAQ</h2>

            <h3 className="bop-guida-coge__sottotitolo-sezione">Posso cambiare la natura di un conto dopo averlo creato?</h3>
            <p className="bop-guida-coge__paragrafo">
              No. La natura è fissata sulla Classe (livello 1) e si propaga ai discendenti; non
              è modificabile. Il sottotipo invece sì, si può cambiare.
            </p>

            <h3 className="bop-guida-coge__sottotitolo-sezione">Quanti registri devo creare?</h3>
            <p className="bop-guida-coge__paragrafo">
              Nessuno: BOP crea e gestisce <strong>automaticamente l'unico Giornale</strong> della
              società (il libro giornale è unico, art. 2216 c.c.). La pagina <em>Registri
              contabili</em> è di sola lettura. Cassa e conti correnti si gestiscono come
              <strong> conti</strong> nel piano dei conti; i registri IVA (obbligatori) nei
              <strong> Sezionali IVA</strong>.
            </p>

            <h3 className="bop-guida-coge__sottotitolo-sezione">Perché una fattura ha due numeri?</h3>
            <p className="bop-guida-coge__paragrafo">
              Perché riceve un numero dal registro contabile (la progressione del libro, es. il
              giornale) e un numero dal sezionale IVA (la progressione del registro IVA). Sono
              numerazioni indipendenti previste dalla normativa.
            </p>

            <h3 className="bop-guida-coge__sottotitolo-sezione">Devo per forza usare una causale?</h3>
            <p className="bop-guida-coge__paragrafo">
              No, la causale è facoltativa: puoi inserire le righe a mano. Ma usarla velocizza il
              lavoro e riduce gli errori, perché precompila conti, registro e sezionale.
            </p>

            <h3 className="bop-guida-coge__sottotitolo-sezione">Il software stampa il bilancio CEE pronto da depositare?</h3>
            <p className="bop-guida-coge__paragrafo">
              Non ancora: la stampa automatica di bilancio di verifica/Stato
              Patrimoniale/Conto Economico è una funzione in roadmap. I dati però sono tutti
              presenti e riclassificabili per natura (vedi sezione 10). Il PDF del bilancio
              ufficiale si archivia in <em>Management → Upload Documenti → Bilanci</em>.
            </p>

            <h3 className="bop-guida-coge__sottotitolo-sezione">Cosa succede se sbaglio una liquidazione già definitiva?</h3>
            <p className="bop-guida-coge__paragrafo">
              Puoi riaprirla, ma solo se la liquidazione del periodo successivo non è ancora
              definitiva. Dopo le correzioni, la rendi di nuovo definitiva.
            </p>
          </section>

          {/* 14. GLOSSARIO */}
          <section id="glossario" className="bop-guida-coge__sezione">
            <h2 className="bop-guida-coge__sezione-titolo">14. Glossario</h2>
            <dl className="bop-guida-coge__glossario">
              <dt>Partita doppia</dt>
              <dd>Metodo di registrazione per cui ogni operazione è scritta su almeno due conti, con totale Dare uguale a totale Avere.</dd>

              <dt>Sottoconto (foglia movimentabile)</dt>
              <dd>Il conto di ultimo livello su cui si registrano effettivamente i movimenti. Solo i sottoconti sono movimentabili.</dd>

              <dt>Natura</dt>
              <dd>Classificazione del conto (Attività, Passività, Patrimonio netto, Costi, Ricavi, Conti d'ordine). Si imposta sulla Classe e si eredita. Determina la collocazione in bilancio.</dd>

              <dt>Sottotipo</dt>
              <dd>Classificazione funzionale del sottoconto (Generico, Cliente, Fornitore, Banca, Cassa, IVA, Erario, Personale, Patrimonio). Collega il conto ad anagrafiche e automazioni.</dd>

              <dt>Registro contabile</dt>
              <dd>Libro di prima nota (Giornale, Cassa, Banca, Altro) con numerazione progressiva annuale propria.</dd>

              <dt>Sezionale IVA</dt>
              <dd>Registro IVA per legge (Vendite, Acquisti, Corrispettivi) con numerazione propria. Base della liquidazione IVA.</dd>

              <dt>Causale contabile</dt>
              <dd>Modello di scrittura che precompila conti, registro, sezionale e righe Dare/Avere per un tipo di operazione.</dd>

              <dt>Quadratura</dt>
              <dd>Condizione per cui il totale Dare è uguale al totale Avere (tolleranza un centesimo). Necessaria per confermare una scrittura.</dd>

              <dt>Storno</dt>
              <dd>Scrittura inversa che neutralizza una scrittura confermata; l'originale passa allo stato "Stornata".</dd>

              <dt>Liquidazione IVA</dt>
              <dd>Calcolo periodico (mensile/trimestrale) dell'IVA da versare o a credito, ottenuto da IVA vendite meno IVA acquisti, al netto del credito pregresso.</dd>

              <dt>Bilancio di verifica</dt>
              <dd>Elenco di tutti i conti con i rispettivi saldi Dare/Avere a una certa data; punto di partenza per Stato Patrimoniale e Conto Economico.</dd>

              <dt>Stato Patrimoniale / Conto Economico</dt>
              <dd>I due prospetti del bilancio civilistico: il primo fotografa attività, passività e patrimonio netto; il secondo riepiloga costi e ricavi e ne ricava utile o perdita.</dd>
            </dl>
          </section>

          {/* 15. RIFERIMENTI */}
          <section id="riferimenti" className="bop-guida-coge__sezione">
            <h2 className="bop-guida-coge__sezione-titolo">15. Riferimenti</h2>
            <p className="bop-guida-coge__paragrafo">Le pagine del gestionale citate in questa guida:</p>
            <ul className="bop-guida-coge__lista">
              <li><strong>Piano dei conti CO.GE</strong> — Gestione Amministrativa → Contabilità Generale.</li>
              <li><strong>Sezionali IVA</strong>, <strong>Registri contabili</strong>, <strong>Causali contabili</strong> — stesso menu.</li>
              <li><strong>Scritture contabili</strong> e <strong>Liquidazioni IVA</strong> — stesso menu.</li>
              <li><strong>Codici IVA</strong> e <strong>Banche aziendali</strong> — Gestione Amministrativa → Parametri Amministrazione.</li>
              <li><strong>Bilanci</strong> (archivio PDF) — Management → Upload Documenti.</li>
            </ul>
            <p className="bop-guida-coge__paragrafo">
              Per dubbi su un caso specifico, confrontati con il responsabile amministrativo o
              apri una richiesta in <em>Assistenza → Richieste assistenza</em>.
            </p>
          </section>

        </main>

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
